<script lang="ts" setup>
import type { Location } from 'vue-router';

import debounce from 'lodash-es/debounce';
// @ts-expect-error vue2-teleport types
import Vue2Teleport from 'vue2-teleport';
import { useRoute, useRouter } from 'vue-router/composables';

import { activeRequests } from '@/composables/global.request-counter';
import { provideReactiveViewport } from '@/composables/useGlobalReactiveViewport';

import { useProfileStore } from './store/profile';
import { useSidepanelStore } from './store/side-panel';

import ThemeLoader from '@/components/common/ThemeLoader.vue';
import CircleAlertIcon from '@/components/icons/CircleAlertIcon.vue';
import CircleSuccessIcon from '@/components/icons/CircleSuccessIcon.vue';

const appBarHeight = ref(0);
const isLoading = ref(false);

const profileStore = useProfileStore();
const sidepanelStore = useSidepanelStore();
const visibleSidePanel = computed(() => sidepanelStore.visibleSidePanel);

provideReactiveViewport();

function onAppBarResize(data: ResizeObserverEntry['contentRect']) {
  appBarHeight.value = data.height ?? 0;
}

const hideLoaderDebouncer = debounce((value) => {
  if (value === 0) {
    isLoading.value = false;
  }
}, 100);

// при последовательных запросах лоадер дёргается (состояние сбрасывается до 0), делаем отложенный сброс
watch(activeRequests, (value) => {
  if (value > 0) {
    if (!isLoading.value) {
      isLoading.value = true;
    }

    hideLoaderDebouncer.cancel();
  }
  else if (isLoading.value) {
    hideLoaderDebouncer(value);
  }
});

const router = useRouter();
const route = useRoute();

watch(() => profileStore.sessionExpired, (nv) => {
  if (!nv)
    return;

  sidepanelStore.despawn(null, true); // fixme: это озачает "закрыть все активные -- странно, да?"

  if (profileStore.isQuickAccessOn && profileStore.deviceIdSupported) {
    const destination = { name: 'quickAccess', params: { companyId: profileStore.companyId } } as Location;

    !route.fullPath.includes('redirectTo') && (destination.query = { redirectTo: route.fullPath });
    router.push(destination);
    return;
  }

  router.push({ name: 'login' });
}, { immediate: true });
</script>

<template>
  <v-app
    :style="{
      '--app-bar-height': `${appBarHeight}px`,
    }"
  >
    <v-overlay :value="isLoading" color="bnw900" opacity="0.1" z-index="65535">
      <ThemeLoader />
    </v-overlay>

    <Vue2Teleport to="body > #app">
      <NotificationsGroup
        v-slot="{
          visible,
        }"
        data-disallow-inert
        style="left: 50%; top: 1rem; transform: translateX(-50%)"
      >
        <BaseAlert
          v-for="notificationItem in visible"
          :key="`notification-${notificationItem.id}`"
          dismissible
          elevated
          :type="notificationItem.type"
          @dismiss="() => notificationItem.remove()"
          v-on="notificationItem.listeners"
        >
          <CircleSuccessIcon v-if="notificationItem.type === 'success'" />
          <CircleAlertIcon v-else />
          <span>{{ notificationItem.text }}</span>
        </BaseAlert>
      </NotificationsGroup>
    </Vue2Teleport>

    <router-view class="fixed-layout" name="bar" @resize="onAppBarResize" />

    <Transition name="slide-x-transition" mode="out-in">
      <router-view class="flex-grow-1 fixed-layout" />
    </Transition>

    <router-view class="fixed-layout" name="footer" />

    <component :is="visibleSidePanel.component" v-if="visibleSidePanel" :panel="visibleSidePanel" v-bind="visibleSidePanel.props" />
  </v-app>
</template>

<style lang="scss">
@import '@/styles/main.scss';

div#app.v-application {
  background-color: var(--app-background-color, transparent);
  transition: background-color 150ms ease-in;
}

.v-application--wrap {
  height: 100%;
  justify-content: end;
}
</style>
